import React, {useEffect, useState} from 'react';
import {useTranslations} from "../../lang";
import {Button, Col, DatePicker, Drawer, Form, Modal, Row, Select, Spin, Table} from "antd";
import api from "../../api";
import dayjs from "dayjs";
import CropInfo from "../../Components/CropInfo";
import {ArrowLeftOutlined, ArrowRightOutlined, CalendarOutlined, CheckCircleFilled, ClockCircleOutlined, EditOutlined, PlusOutlined, SettingOutlined} from "@ant-design/icons";
import './style.css'
import PlantingDrawer from "../../Components/PlantingDrawer";
import 'dayjs/locale/nl';
import TaskAssigneeSelect from "../../Components/TaskAssigneeSelect";
import {useSearchParams} from "react-router-dom";
import {useAuth} from "../../auth";
import {DATE_FORMAT} from '../../constants'
import {noteToParagraph} from "../../helpers";
import {formatCropMeasurement} from "../../utils/measurements";
import CreateTaskModal from "../../Components/CreateTaskModal";
import EditTaskModal from "../../Components/EditTaskModal";
import TaskSettingsModal from "../../Components/TaskSettingsModal";

dayjs.locale('nl');

export function formatTaskCropSpacing(crop, field, accountMeasurementSystem) {
    return formatCropMeasurement(crop, field, accountMeasurementSystem);
}

export default function Tasks() {
    const {translate} = useTranslations()

    const [searchParams] = useSearchParams()

    const {plan, measurementSystem} = useAuth()

    const [isLoading, setIsLoading] = useState(false)
    const [tasks, setTasks] = useState([])
    const [users, setUsers] = useState([])
    const [gardens, setGardens] = useState([])
    const [taskDrawerOpen, setTaskDrawerOpen] = useState(false)
    const [plantingDrawerOpen, setPlantingDrawerOpen] = useState(false)
    const [openTask, setOpenTask] = useState(null)
    const [createTaskModalOpen, setCreateTaskModalOpen] = useState(false)
    const [editTaskModalOpen, setEditTaskModalOpen] = useState(false)
    const [settingsModalOpen, setSettingsModalOpen] = useState(false)
    const [snoozeModalOpen, setSnoozeModalOpen] = useState(false)
    const [snoozeTask, setSnoozeTask] = useState(null)
    const [page, setPage] = useState(0)
    const [filters, setFilters] = useState({
        end_date: dayjs().endOf('week').add(page, 'weeks').format("YYYY-MM-DD"),
        is_completed: false,
        garden_id: searchParams.get('gardenId') ? parseInt(searchParams.get('gardenId')) : null,
    })
    const [dateRange, setDateRange] = useState("week");
    const rangeLabels = {
        'week': 'week',
        'month': 'maand'
    }

    useEffect(() => {
        setIsLoading(true)
        api.getTeamMembers().then(setUsers)
        api.gardens().then(setGardens)
        api.getTasks(filters).then(setTasks).finally(() => {
            setIsLoading(false)
        })
    }, []);

    useEffect(() => {
        api.getTasks(filters).then(setTasks)
    }, [filters, taskDrawerOpen]);

    function renderDueDate(task) {
        const color = dayjs(task.due_at).isBefore(dayjs(), 'day') ? 'red' : 'default';
        return <span style={{color: color}}>{dayjs(task.due_at).format(DATE_FORMAT)}</span>
    }

    function setStartAndEndDate(range, p) {
        setPage(p)
        setDateRange(range)
        if (range === null) {
            setFilters({
                ...filters,
                start_date: null,
                end_date: null
            })
            return
        }

        if (range === 'past') {
            setFilters({
                ...filters,
                start_date: null,
                end_date: dayjs().startOf('day').format("YYYY-MM-DD"),
            })
            return;
        }

        if (range === "week") {
            setFilters({
                ...filters,
                start_date: dayjs().add(p, 'weeks').startOf('week').format("YYYY-MM-DD"),
                end_date: dayjs().add(p, 'weeks').endOf('week').format("YYYY-MM-DD")

            })
            return;
        }

        if (range === "month") {
            setFilters({
                ...filters,
                start_date: dayjs().add(p, 'month').startOf('month').format("YYYY-MM-DD"),
                end_date: dayjs().add(p, 'month').endOf('month').format("YYYY-MM-DD")
            })
            return;
        }

        if (range === "year") {
            setFilters({
                ...filters,
                start_date: dayjs().startOf('year').add(p, 'year').format("YYYY-MM-DD"),
                end_date: dayjs().endOf('year').add(p, 'year').format("YYYY-MM-DD")
            })
            return;
        }
    }

    function handleToggleTask(taskId, currentCompleted) {
        api.toggleTask(taskId, !currentCompleted).then(() => {
            setTasks(tasks.map(t => {
                if (t.id === taskId) {
                    return {...t, is_completed: !t.is_completed}
                }
                return t
            }))
        })
    }

    async function handleSnooze(days) {
        if (!snoozeTask) return
        try {
            await api.snoozeTask(snoozeTask.id, days)
        } finally {
            setSnoozeModalOpen(false)
            setSnoozeTask(null)
        }
        const tasks = await api.getTasks(filters)
        setTasks(tasks)
    }

    const columns = [
        {
            responsive: ['md'],
            title: 'Taak',
            key: 'completed_at',
            render: (_, task) => (
                <span>{task.title}</span>
            )
        },
        {
            title: 'Tuin',
            dataIndex: 'garden',
            key: 'garden',
            render(_, task) {
                return task.garden.name
            },
            responsive: ['md']
        },
        {
            title: 'Bed',
            dataIndex: 'bed',
            key: 'bed',
            render(_, task) {
                return task?.planting?.bed?.name || task?.bed?.name
            },
            responsive: ['md']
        },
        {
            title: 'Wat',
            dataIndex: 'crop',
            key: 'crop_md',
            responsive: ['md'],
            render(_, task) {
                if (task.planting) {
                    return (
                        <a onClick={() => {
                            setTaskDrawerOpen(true)
                            setOpenTask(task)
                        }}>
                            {task.planting.rows * task.planting.plants_per_row}x {task.planting.crop.name}
                        </a>
                    )
                }
                return (
                    <a onClick={() => {
                        setTaskDrawerOpen(true)
                        setOpenTask(task)
                    }}>
                        {task.description || '-'}
                    </a>
                )
            }
        },
        {
            title: 'Wat',
            dataIndex: 'crop',
            key: 'crop',
            responsive: ['xs'],
            render(_, task) {
                return (
                    <>
                        <Row>
                            <Col span={12}>
                                <Button
                                    size="small"
                                    type={task.is_completed ? "primary" : "default"}
                                    style={task.is_completed ? {backgroundColor: '#52c41a', borderColor: '#52c41a'} : {}}
                                    onClick={() => handleToggleTask(task.id, task.is_completed)}
                                >
                                    <CheckCircleFilled /> Voltooid
                                </Button>
                            </Col>
                            <Col span={12} className={"text-right"}>
                                <p>
                                    {renderDueDate(task)}
                                    {!task.is_completed && (
                                        <Button
                                            size="small"
                                            className="mar-l-1"
                                            onClick={() => {
                                                setSnoozeTask(task)
                                                setSnoozeModalOpen(true)
                                            }}
                                        >
                                            <ClockCircleOutlined /> Snooze
                                        </Button>
                                    )}
                                </p>
                            </Col>

                        </Row>
                        <Row>
                            <Col span={24}>
                                <p>
                                    <a onClick={() => {
                                        setTaskDrawerOpen(true)
                                        setOpenTask(task)
                                    }}>
                                        {task.planting
                                            ? `${task.planting.rows * task.planting.plants_per_row}x ${task.planting.crop.name} ${task.planting.crop.variety}`
                                            : (task.title || '-')
                                        }
                                    </a>
                                </p>
                                <p>
                                    {task.garden.name}{task.planting?.bed?.name ? ` - ${task.planting.bed.name}` : (task.bed?.name ? ` - ${task.bed.name}` : '')}
                                </p>
                            </Col>
                        </Row>
                    </>

                )
            }
        },
        {
            title: 'Wanneer',
            dataIndex: 'due_at',
            responsive: ['md'],
            key: 'due_at',
            render(_, task) {
                return (
                    <span>
                        {renderDueDate(task)}
                        {!task.is_completed && (
                            <Button
                                size="small"
                                className="mar-l-1"
                                onClick={() => {
                                    setSnoozeTask(task)
                                    setSnoozeModalOpen(true)
                                }}
                            >
                                <ClockCircleOutlined /> Snooze
                            </Button>
                        )}
                    </span>
                )
            }
        },
        {
            title: '',
            key: 'actions',
            responsive: ['md'],
            render(_, task) {
                return (
                    <Button
                        size="small"
                        type={task.is_completed ? "primary" : "default"}
                        style={task.is_completed ? {backgroundColor: '#52c41a', borderColor: '#52c41a'} : {}}
                        onClick={() => handleToggleTask(task.id, task.is_completed)}
                    >
                        <CheckCircleFilled /> Voltooid
                    </Button>
                )
            }
        }
    ]

    if (plan.id === 2) {
        columns.push({
            title: 'Wie',
            key: 'user_id',
            render: (_, task) => (
                <TaskAssigneeSelect users={users} task={task}/>
            ),
            responsive: ['md']
        })
    }

    return (
        <div>
            <Form layout={"vertical"}>
                <Row gutter={[18, 18]}>
                    <Col md={4} xs={12}>
                        <Form.Item label={"Wanneer"}>
                            <Select defaultValue={"week"} onChange={setStartAndEndDate} options={[
                                {value: null, label: 'Alles'},
                                {value: 'past', label: 'Verleden / te laat'},
                                {value: 'week', label: 'Per week'},
                                {value: 'month', label: 'Per maand'},
                            ]}>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col md={4} xs={12}>
                        <Form.Item label={"tuin"}>
                            <Select defaultValue={filters.garden_id} onChange={(e) => {
                                setFilters({
                                    ...filters,
                                    garden_id: e
                                })
                            }} options={[{value: null, label: 'Alle tuinen'}].concat(gardens.map((garden) => {
                                return {
                                    value: garden.id,
                                    label: garden.name
                                }
                            }))}>
                            </Select>
                        </Form.Item>
                    </Col>
                    {plan.id === 2 && (
                        <Col md={4} xs={12}>
                            <Form.Item label={"verantwoordelijke"}>
                                <Select defaultValue={null} onChange={(e) => {
                                    setFilters({
                                        ...filters,
                                        user_id: e
                                    })
                                }} options={[{value: null, label: 'Iedereen'}].concat(
                                    users.map((user) => {
                                        return {
                                            value: user.id,
                                            label: user.name
                                        }
                                    }))}>
                                </Select>
                            </Form.Item>
                        </Col>
                    )}
                    <Col md={4} xs={12}>
                        <Form.Item label={"type taak"}>
                            <Select defaultValue={null} onChange={(e) => {
                                setFilters({
                                    ...filters,
                                    task_type: e
                                })
                            }} options={[
                                {value: null, label: 'Alle typen taken'},
                                {value: 'sow', label: 'Zaaien'},
                                {value: 'prick', label: 'Verspenen'},
                                {value: 'plant', label: 'Planten'},
                                {value: 'harvest', label: 'Oogsten'},
                                {value: 'custom', label: 'Eigen taken'},
                            ]}>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col md={4} xs={12}>
                        <Form.Item label={"voltooid"}>
                            <Select defaultValue={filters.is_completed} onChange={(e) => {
                                setFilters({
                                    ...filters,
                                    is_completed: e
                                })
                            }} options={[
                                {value: null, label: 'Alle taken'},
                                {value: true, label: 'Voltooid'},
                                {value: false, label: 'Niet voltooid'},
                            ]}>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col md={4} xs={12}>
                        <Form.Item label={" "}>
                            <Button type="primary" onClick={() => setCreateTaskModalOpen(true)}>
                                <PlusOutlined /> Nieuwe taak
                            </Button>
                        </Form.Item>
                    </Col>
                    <Col md={4} xs={12}>
                        <Form.Item label={" "}>
                            <Button onClick={() => setSettingsModalOpen(true)}>
                                <SettingOutlined /> Taak instellingen
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>


            {(dateRange && dateRange !== 'past') && (
                <Row className={"mar-b-1"}>
                    <Col span={24} className={"text-center"}>
                        <h1>{translate('Takenlijst')}</h1>
                        <h2>
                            {dayjs(filters.start_date).format(DATE_FORMAT)} t/m {dayjs(filters.end_date).format(DATE_FORMAT)}
                        </h2>
                        {dateRange === 'week' && (
                            <p>(week {dayjs(filters.start_date).week()})</p>
                        )}
                    </Col>
                    <Col span={12}>
                        <Button onClick={() => {
                            setStartAndEndDate(dateRange, page - 1)
                        }}><ArrowLeftOutlined/> vorige {rangeLabels[dateRange]}</Button>
                    </Col>
                    <Col className={"text-right"} span={12}>
                        <Button onClick={() => {
                            setStartAndEndDate(dateRange, page + 1)
                        }}>volgende {rangeLabels[dateRange]} <ArrowRightOutlined/></Button>
                    </Col>
                </Row>
            )}

            <Drawer open={plantingDrawerOpen} size={"large"} onClose={() => {
                setPlantingDrawerOpen(false)
            }}>
                {openTask && openTask.planting && (
                    <PlantingDrawer
                        gardenId={openTask.garden_id}
                        plantingId={openTask.planting_id}
                        bedId={openTask.planting.bed_id}
                        onCloseDrawer={() => {
                            setPlantingDrawerOpen(false)
                        }}/>
                )}
            </Drawer>

            <Drawer open={taskDrawerOpen} size={"large"} onClose={() => setTaskDrawerOpen(false)}>
                {openTask && (
                    <>
                        <Row>
                            <Col span={12}>
                                {!openTask.is_completed && (
                                    <Button type={"primary"} className={"mar-b-1"} onClick={() => {
                                        api.toggleTask(openTask.id, true).then(() => {
                                            setTaskDrawerOpen(false)
                                            api.getTasks(filters).then(setTasks)
                                        })
                                    }}><CheckCircleFilled/> Markeer als gedaan</Button>
                                )}

                                <Button type={"default"}
                                        onClick={() => {
                                            setEditTaskModalOpen(true)
                                        }}
                                        className={"mar-b-1 mar-l-1"}>
                                    <EditOutlined/> Bewerken
                                </Button>
                            </Col>
                            <Col span={12} className={"text-right"}>
                                {openTask.planting && (
                                    <Button type={"default"}
                                            onClick={() => {
                                                setPlantingDrawerOpen(true)
                                            }}
                                            className={"mar-b-1"}>
                                        <CalendarOutlined/> Open planner
                                    </Button>
                                )}
                            </Col>
                        </Row>

                        {openTask.planting ? (
                            <>
                                <h1>{openTask.title}: {openTask.planting.crop.name} - {openTask.planting.crop.variety}</h1>
                                <CropInfo crop={openTask.planting.crop}/>
                                <hr/>
                                {plan.id === 2 && (
                                    <Row className={"task-detail-row"}>
                                        <Col span={8}>Wie</Col>
                                        <Col span={16}>
                                            <TaskAssigneeSelect users={users} task={openTask}/>
                                        </Col>
                                    </Row>
                                )}
                                <Row className={"task-detail-row"}>
                                    <Col span={8}>Wat</Col>
                                    <Col span={16}>
                                        {openTask.planting.rows * openTask.planting.plants_per_row}x {openTask.planting.crop.name} - {openTask.planting.crop.variety}
                                    </Col>
                                </Row>
                                <Row className={"task-detail-row"}>
                                    <Col span={8}>Waar</Col>
                                    <Col span={16}>Tuin: <b>{openTask.garden.name}</b>, bed: <b>{openTask.planting.bed.name}</b></Col>
                                </Row>
                                <Row className={"task-detail-row"}>
                                    <Col span={8}>Wanneer</Col>
                                    <Col span={16}>{dayjs(openTask.due_at).format(DATE_FORMAT)}</Col>
                                </Row>
                                <Row className={"task-detail-row"}>
                                    <Col span={8}>Aantal planten</Col>
                                    <Col span={16}>{openTask.planting.rows * openTask.planting.rows}</Col>
                                </Row>
                                <Row className={"task-detail-row"}>
                                    <Col span={8}>Aantal rijen</Col>
                                    <Col span={16}>{openTask.planting.rows},
                                        rijafstand {formatTaskCropSpacing(openTask.planting.crop, 'row_distance', measurementSystem)}</Col>
                                </Row>
                                <Row className={"task-detail-row"}>
                                    <Col span={8}>Planten per rij</Col>
                                    <Col span={16}>{openTask.planting.plants_per_row},
                                        plantafstand {formatTaskCropSpacing(openTask.planting.crop, 'plant_distance', measurementSystem)}</Col>
                                </Row>
                                {openTask.planting.note && (

                                    <Row className={"task-detail-row"}>
                                        <Col span={8}>Notities planning</Col>
                                        <Col span={16}>{noteToParagraph(openTask.planting.note)}</Col>
                                    </Row>
                                )}
                                {openTask.planting.crop.note && (
                                    <Row className={"task-detail-row"}>
                                        <Col span={8}>Notities gewas</Col>
                                        <Col span={16}>{noteToParagraph(openTask.planting.crop.note)}</Col>
                                    </Row>
                                )}
                                <Row>
                                    <Col span={24}>
                                        <p>
                                            {openTask.description}
                                        </p>
                                        <p>
                                            {openTask.planting.notes}
                                        </p>
                                        <p>
                                            {openTask.planting.crop.notes}
                                        </p>
                                    </Col>
                                </Row>
                            </>
                        ) : (
                            <>
                                <h1>{openTask.title}</h1>
                                <hr/>
                                {plan.id === 2 && (
                                    <Row className={"task-detail-row"}>
                                        <Col span={8}>Wie</Col>
                                        <Col span={16}>
                                            <TaskAssigneeSelect users={users} task={openTask}/>
                                        </Col>
                                    </Row>
                                )}
                                <Row className={"task-detail-row"}>
                                    <Col span={8}>Waar</Col>
                                    <Col span={16}>
                                        Tuin: <b>{openTask.garden?.name}</b>
                                        {openTask.bed && <>, bed: <b>{openTask.bed.name}</b></>}
                                    </Col>
                                </Row>
                                <Row className={"task-detail-row"}>
                                    <Col span={8}>Wanneer</Col>
                                    <Col span={16}>{dayjs(openTask.due_at).format(DATE_FORMAT)}</Col>
                                </Row>
                                {openTask.description && (
                                    <Row className={"task-detail-row"}>
                                        <Col span={8}>Beschrijving</Col>
                                        <Col span={16}>{noteToParagraph(openTask.description)}</Col>
                                    </Row>
                                )}
                            </>
                        )}
                    </>
                )}
            </Drawer>

            {isLoading && (
                <Spin />
            )}

            <Table columns={columns} rowKey={"id"} dataSource={tasks} pagination={false}/>

            <CreateTaskModal
                open={createTaskModalOpen}
                onCancel={() => setCreateTaskModalOpen(false)}
                onSuccess={() => api.getTasks(filters).then(setTasks)}
                gardens={gardens}
            />

            <TaskSettingsModal
                open={settingsModalOpen}
                onCancel={() => setSettingsModalOpen(false)}
            />

            <EditTaskModal
                open={editTaskModalOpen}
                onCancel={() => setEditTaskModalOpen(false)}
                onSuccess={() => {
                    api.getTasks(filters).then(setTasks)
                    setTaskDrawerOpen(false)
                }}
                task={openTask}
            />

            <Modal
                title="Snooze taak"
                open={snoozeModalOpen}
                onCancel={() => {
                    setSnoozeModalOpen(false)
                    setSnoozeTask(null)
                }}
                footer={null}
            >
                <p>Stel de vervaldatum uit:</p>
                <Row gutter={[12, 12]}>
                    <Col span={12}>
                        <Button block onClick={() => handleSnooze(1)}>Morgen</Button>
                    </Col>
                    <Col span={12}>
                        <Button block onClick={() => handleSnooze(3)}>Over 3 dagen</Button>
                    </Col>
                    <Col span={12}>
                        <Button block onClick={() => handleSnooze(7)}>Over een week</Button>
                    </Col>
                    <Col span={12}>
                        <DatePicker
                            style={{width: '100%'}}
                            placeholder="Kies een datum"
                            disabledDate={(current) => current && current < dayjs().startOf('day')}
                            onChange={(date) => {
                                if (date) {
                                    const days = date.diff(dayjs().startOf('day'), 'day')
                                    handleSnooze(days)
                                }
                            }}
                        />
                    </Col>
                </Row>
            </Modal>
        </div>
    );
}